import { AuthFooterLink, AuthInput, AuthMessage, AuthSubmitButton, Button } from '@protoss/ui';
import * as React from 'react';
import { Navigate } from 'react-router-dom';
import { AuthCard, AuthHeader, AuthLayout } from '../../components/auth';
import { useAuth } from '../../contexts/AuthContext';
import { getPreAuthConfig } from '../../lib/preAuthConfig';
import { withDevWorkspaceParam } from '../../lib/url';
import './auth-pages.css';

/**
 * Forgot Password Page
 *
 * Sends a Firebase password reset email (AuthContext.sendPasswordReset). The
 * link lands on /reset-password via /auth/action.
 *
 * UX Note: We do not reveal whether an email exists in the system for security reasons.
 * An unknown email shows the same success message; real failures (network, rate
 * limit, misconfigured continue URL) are surfaced so the user knows nothing was sent.
 */

interface FormData {
  email: string;
}

interface FormErrors {
  email?: string;
}

type FormState = 'idle' | 'loading' | 'success' | 'error';

type SendResult = { ok: true } | { ok: false; field?: 'email'; message: string };

async function requestPasswordReset(
  sendPasswordReset: (email: string) => Promise<void>,
  email: string
): Promise<SendResult> {
  try {
    await sendPasswordReset(email);
    return { ok: true };
  } catch (error) {
    const code = (error as { code?: string } | null)?.code ?? '';
    // Same message as a real send, so the page never reveals which accounts exist.
    if (code === 'auth/user-not-found') return { ok: true };
    if (code === 'auth/invalid-email' || code === 'auth/missing-email') {
      return { ok: false, field: 'email', message: 'Please enter a valid email address' };
    }
    if (code === 'auth/too-many-requests' || code === 'auth/quota-exceeded') {
      return {
        ok: false,
        message: 'Too many reset requests. Please wait a few minutes and try again.',
      };
    }
    if (code === 'auth/network-request-failed') {
      return {
        ok: false,
        message: 'Unable to reach the server. Check your connection and try again.',
      };
    }
    console.error('Password reset email failed', error);
    return { ok: false, message: 'Unable to send reset email. Please try again later.' };
  }
}

export function ForgotPasswordPage() {
  const { sendPasswordReset } = useAuth();

  // Form state
  const [formData, setFormData] = React.useState<FormData>({
    email: '',
  });

  const [errors, setErrors] = React.useState<FormErrors>({});
  const [formState, setFormState] = React.useState<FormState>('idle');
  const [generalError, setGeneralError] = React.useState<string | null>(null);
  // Keeps the "Check your email" view up while a resend is in flight.
  const [hasSent, setHasSent] = React.useState(false);

  if (!getPreAuthConfig().allowedMethods.includes('password')) {
    return <Navigate to="/signin" replace />;
  }

  // Validation
  const validateEmail = (email: string): string | undefined => {
    if (!email.trim()) {
      return 'Email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {
      email: validateEmail(formData.email),
    };

    setErrors(newErrors);
    return !newErrors.email;
  };

  // Handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for this field when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }

    // Clear general error when user modifies form
    if (generalError) {
      setGeneralError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Clear previous errors
    setGeneralError(null);

    // Validate form
    if (!validateForm()) {
      return;
    }

    await sendResetEmail();
  };

  const sendResetEmail = async () => {
    setFormState('loading');
    const result = await requestPasswordReset(sendPasswordReset, formData.email.trim());
    if (result.ok) {
      setHasSent(true);
      setFormState('success');
      return;
    }
    // Errors render on the form view, so a failed resend returns there too.
    setFormState('error');
    if (result.field) {
      setErrors({ [result.field]: result.message });
    } else {
      setGeneralError(result.message);
    }
  };

  const isLoading = formState === 'loading';
  const isSuccess = formState === 'success' || (isLoading && hasSent);

  return (
    <AuthLayout>
      <AuthCard>
        {isSuccess ? (
          <>
            <AuthHeader title="Check your email" />

            <div className="auth-form-container">
              <AuthMessage variant="info">
                If an account exists for that email, we sent a password reset link. Please check
                your inbox and spam folder.
              </AuthMessage>

              <div className="auth-form">
                <Button
                  variant="outline"
                  size="md"
                  onClick={() => void sendResetEmail()}
                  disabled={isLoading}
                >
                  {isLoading ? 'Sending...' : 'Resend email'}
                </Button>
              </div>
            </div>

            <div className="auth-footer-text">
              <AuthFooterLink href={withDevWorkspaceParam('/signin')}>
                Back to sign in
              </AuthFooterLink>
            </div>
          </>
        ) : (
          <>
            <AuthHeader
              title="Forgot your password?"
              subtitle="Enter your email and we'll send a reset link"
            />

            <div className="auth-form-container">
              {generalError && <AuthMessage variant="error">{generalError}</AuthMessage>}

              <form onSubmit={handleSubmit} className="auth-form" noValidate>
                <AuthInput
                  type="email"
                  name="email"
                  label="Email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  error={errors.email}
                  autoComplete="email"
                  disabled={isLoading}
                />

                <AuthSubmitButton isLoading={isLoading}>Send reset link</AuthSubmitButton>
              </form>
            </div>

            <div className="auth-footer-text">
              Remember your password?{' '}
              <AuthFooterLink href={withDevWorkspaceParam('/signin')}>Sign in</AuthFooterLink>
            </div>
          </>
        )}
      </AuthCard>
    </AuthLayout>
  );
}
